import { useState, useRef, useCallback, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://178.105.190.198.nip.io';

// Motore voce completo, attivato SOLO da pulsante (mai in background):
//  - STT: registrazione microfono → /api/v1/voice/transcribe (faster-whisper).
//  - TTS: Edge TTS lato backend (voce it-IT-IsabellaNeural) → WAV riprodotto
//    via AudioContext, con possibilità di interruzione (barge-in).
//  - Conversazione continua: dopo la risposta riapre il microfono per il
//    follow-up, finché non premi stop.
//  - Interruzione: interrupt() ferma la voce di JARVIS (barge-in).
//
// Stati: 'idle' | 'listening' | 'thinking' | 'speaking'.
// onUtterance(text) viene chiamato quando l'utente ha finito di parlare e
// abbiamo la trascrizione: il chiamante invia il messaggio all'agent e, quando
// arriva la risposta, chiama speak(reply).
export function useVoice({ onUtterance } = {}) {
  const [status, setStatus]       = useState('idle');
  const [sessionOn, setSessionOn] = useState(false);

  const sessionRef  = useRef(false);
  const statusRef   = useRef('idle');
  const recRef      = useRef(null);
  const streamRef   = useRef(null);
  const chunksRef   = useRef([]);
  const onUtterRef  = useRef(onUtterance);
  const audioCtxRef = useRef(null);   // TTS: contesto audio riusato
  const sourceRef   = useRef(null);   // TTS: sorgente in riproduzione (per interrompere)
  const vadCtxRef    = useRef(null);  // VAD: AudioContext di analisi del microfono
  const vadTimerRef  = useRef(null);  // VAD: loop di polling del livello audio
  const vadSourceRef = useRef(null);  // VAD: MediaStreamSource collegata all'analyser

  useEffect(() => { onUtterRef.current = onUtterance; }, [onUtterance]);

  const setSt = (s) => { statusRef.current = s; setStatus(s); };

  // Ferma la voce di JARVIS in riproduzione (Edge TTS via AudioContext).
  const stopAudio = () => {
    try { if (sourceRef.current) { sourceRef.current.onended = null; sourceRef.current.stop(); } } catch (e) {}
    sourceRef.current = null;
  };

  // Spegne la VAD (loop + AudioContext di analisi).
  const stopVAD = () => {
    if (vadTimerRef.current) { clearInterval(vadTimerRef.current); vadTimerRef.current = null; }
    try { if (vadSourceRef.current) vadSourceRef.current.disconnect(); } catch (e) {}
    vadSourceRef.current = null;
    try { if (vadCtxRef.current) vadCtxRef.current.close(); } catch (e) {}
    vadCtxRef.current = null;
  };

  const stopRecorderRaw = () => {
    stopVAD();
    try { if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop(); } catch (e) {}
    try { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) {}
    recRef.current = null;
    streamRef.current = null;
  };

  const transcribe = async (blob) => {
    try {
      const form = new FormData();
      form.append('file', blob, 'audio.webm');
      const res = await fetch(`${API}/api/v1/voice/transcribe`, { method: 'POST', body: form });
      if (!res.ok) return '';
      const data = await res.json();
      return (data.clean || data.text || '').trim();
    } catch (e) {
      console.error('STT error', e);
      return '';
    }
  };

  const beginListening = useCallback(async () => {
    if (!sessionRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stopVAD();
        try { stream.getTracks().forEach(t => t.stop()); } catch (e) {}
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        chunksRef.current = [];
        if (!sessionRef.current) return;             // sessione chiusa nel frattempo
        setSt('thinking');
        const text = await transcribe(blob);
        if (!text) {                                  // niente parlato utile: riascolta
          if (sessionRef.current) beginListening(); else setSt('idle');
          return;
        }
        if (onUtterRef.current) onUtterRef.current(text);
        // resta 'thinking' finché il chiamante non chiama speak(reply)
      };
      rec.start();
      recRef.current = rec;
      setSt('listening');

      // ── VAD: rileva automaticamente la fine del turno (silenzio > 1.5s),
      // così non serve premere nulla (stile ChatGPT Advanced Voice). ──
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const vctx = new AC();
        vadCtxRef.current = vctx;
        if (vctx.state === 'suspended') { try { await vctx.resume(); } catch (e) {} }
        const source = vctx.createMediaStreamSource(stream);
        vadSourceRef.current = source;
        const analyser = vctx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        const buf = new Float32Array(analyser.fftSize);

        const POLL = 50;            // ms tra i campioni
        const SILENCE_MS = 1500;    // silenzio che chiude il turno
        const MIN_SPEECH_MS = 300;  // parlato minimo prima di poter chiudere
        const CALIB_FRAMES = 7;     // ~350ms di calibrazione del rumore ambiente

        let frames = 0, noiseSum = 0, threshold = 0.02;
        let speechMs = 0, silenceStart = 0, speechStarted = false;

        const tick = () => {
          if (!recRef.current || recRef.current.state !== 'recording') return;
          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
          const rms = Math.sqrt(sum / buf.length);

          // Prima calibra il rumore di fondo, poi imposta la soglia.
          if (frames < CALIB_FRAMES) {
            noiseSum += rms; frames++;
            if (frames === CALIB_FRAMES) {
              threshold = Math.max(0.014, (noiseSum / CALIB_FRAMES) * 2.5 + 0.006);
            }
            return;
          }

          if (rms > threshold) {                 // sta parlando
            speechMs += POLL;
            silenceStart = 0;
            if (speechMs >= MIN_SPEECH_MS) speechStarted = true;
          } else if (speechStarted) {            // silenzio dopo aver parlato
            if (!silenceStart) {
              silenceStart = Date.now();
            } else if (Date.now() - silenceStart >= SILENCE_MS) {
              stopVAD();                         // fine turno automatica
              try { if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop(); } catch (e) {}
            }
          }
        };
        vadTimerRef.current = setInterval(tick, POLL);
      } catch (e) {
        console.warn('VAD non disponibile (uso lo stop manuale col pulsante):', e);
      }
    } catch (e) {
      console.error('Mic error', e);
      sessionRef.current = false;
      setSessionOn(false);
      setSt('idle');
    }
  }, []);

  // L'utente ha finito il suo turno: ferma la registrazione → parte la trascrizione.
  const endTurn = useCallback(() => {
    if (statusRef.current === 'listening' && recRef.current && recRef.current.state !== 'inactive') {
      try { recRef.current.stop(); } catch (e) {}
    }
  }, []);

  // TTS: chiede al backend l'audio WAV (Edge TTS, voce it-IT-IsabellaNeural) e
  // lo riproduce. Al termine, se la sessione è attiva, riapre il microfono.
  const speak = useCallback(async (text) => {
    const relisten = () => { if (sessionRef.current) beginListening(); else setSt('idle'); };
    const t = (text || '').trim();
    if (!t) { relisten(); return; }
    stopAudio();
    setSt('speaking');
    try {
      const res = await fetch(`${API}/api/v1/voice/synthesize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: t }),
      });
      if (!res.ok) { relisten(); return; }
      const buf = await res.arrayBuffer();
      if (statusRef.current !== 'speaking') return;   // interrotto durante il fetch
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') { try { await ctx.resume(); } catch (e) {} }
      const decoded = await ctx.decodeAudioData(buf);
      if (statusRef.current !== 'speaking') return;   // interrotto durante il decode
      const src = ctx.createBufferSource();
      src.buffer = decoded;
      src.connect(ctx.destination);
      src.onended = () => { sourceRef.current = null; relisten(); };
      sourceRef.current = src;
      src.start();
    } catch (e) {
      console.error('TTS error', e);
      relisten();
    }
  }, [beginListening]);

  // Interrompe JARVIS mentre parla; se la sessione è attiva torna in ascolto.
  const interrupt = useCallback(() => {
    stopAudio();
    if (sessionRef.current) beginListening(); else setSt('idle');
  }, [beginListening]);

  const startSession = useCallback(() => {
    sessionRef.current = true;
    setSessionOn(true);
    beginListening();
  }, [beginListening]);

  const stopSession = useCallback(() => {
    sessionRef.current = false;
    setSessionOn(false);
    stopRecorderRaw();
    stopAudio();
    setSt('idle');
  }, []);

  // Pulsante microfono: press-to-start / press-again-to-stop.
  const micToggle = useCallback(() => {
    if (!sessionRef.current) { startSession(); return; }
    if (statusRef.current === 'listening') { endTurn(); return; }
    if (statusRef.current === 'speaking')  { interrupt(); return; }
    // 'thinking' → nessuna azione
  }, [startSession, endTurn, interrupt]);

  // Pulizia all'uscita: ferma microfono e audio, chiudi il contesto audio.
  useEffect(() => {
    return () => {
      sessionRef.current = false;
      stopRecorderRaw();   // include stopVAD()
      stopAudio();
      try { if (audioCtxRef.current) audioCtxRef.current.close(); } catch (e) {}
      audioCtxRef.current = null;
    };
  }, []);

  return { status, sessionOn, micToggle, startSession, stopSession, endTurn, interrupt, speak };
}
